import React, { FC, Fragment, useContext, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import { ProductionValueStep } from 'models/organization';
import { useProductionValueSteps } from 'hooks/useProductionValueSteps';
import { useDepartments } from 'context/departments-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useWorkflowSteps } from 'context/workflow-steps-context';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { isWorkflowValueLinkEnabled } from 'utils/production-value-step-links';
import AddEditProductionValueStepDialog from './add-edit-production-value-step-dialog/add-edit-production-value-step-dialog.component';

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 150 },
    { type: 'text', width: 200 },
    { type: 'text', width: 100 },
    { type: 'button', width: 80 },
];

const ProductionValueSteps: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { departments } = useDepartments();

    const getDepartmentName = (departmentId?: string) => {
        if (!departmentId) return '—';
        return departments.find((department) => department._id === departmentId)?.name || '—';
    };

    // DD-3545: show which workflow steps feed each value step
    const isLinkEnabled = isWorkflowValueLinkEnabled(currentOrgVar()?.customerSettings);
    const { workFlow } = useWorkflowSteps();
    const getLinkedStepNames = (step: ProductionValueStep) =>
        (step.workflowStepIds ?? [])
            .map((stepId) => workFlow?.steps.find((workflowStep) => workflowStep._id === stepId)?.name)
            .filter(Boolean)
            .join(', ') || '—';

    const { productionValueSteps, productionValueStepsLoading, deleteProductionValueStep, deleteStepLoading } =
        useProductionValueSteps();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedStep, setSelectedStep] = useState<ProductionValueStep | null>(null);

    const handleOpenAdd = () => {
        setSelectedStep(null);
        setDialogOpen(true);
    };

    const handleOpenEdit = (step: ProductionValueStep) => {
        setSelectedStep(step);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedStep(null);
    };

    const handleDelete = async (stepId: string) => {
        const result = await deleteProductionValueStep({ stepId });
        if (result) {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:PRODUCTION_VALUE_STEP_DELETED_SUCCESSFULLY'),
            });
        }
    };

    return (
        <Fragment>
            <Box mb={2}>
                <Button startIcon={<AddIcon />} onClick={handleOpenAdd}>
                    {translate('lab-settings:ADD_PRODUCTION_VALUE_STEP')}
                </Button>
            </Box>
            <Paper variant="outlined">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('lab-settings:NAME')}</TableCell>
                                <TableCell>{translate('lab-settings:DEPARTMENT')}</TableCell>
                                {isLinkEnabled && (
                                    <TableCell>{translate('lab-settings:LINKED_WORKFLOW_STEPS')}</TableCell>
                                )}
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {productionValueStepsLoading && (
                                <SkeletonTable columns={skeletonColumns} rows={3} renderRowsOnly={true} />
                            )}
                            {productionValueSteps.length > 0 &&
                                productionValueSteps.map((step) => (
                                    <TableRow
                                        key={step._id}
                                        hover
                                        onClick={() => handleOpenEdit(step)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>{step.name}</TableCell>
                                        <TableCell>{getDepartmentName(step.departmentId)}</TableCell>
                                        {isLinkEnabled && <TableCell>{getLinkedStepNames(step)}</TableCell>}
                                        <TableCell align="right">
                                            <Tooltip title={translate('common:DELETE')}>
                                                <IconButton
                                                    disabled={deleteStepLoading}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(step._id);
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {productionValueSteps.length === 0 && !productionValueStepsLoading && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        {translate('lab-settings:NO_PRODUCTION_VALUE_STEPS_FOUND')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <AddEditProductionValueStepDialog
                open={dialogOpen}
                step={selectedStep ?? undefined}
                onDialogClose={handleDialogClose}
            />
        </Fragment>
    );
};

export default ProductionValueSteps;
